import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import clinaddis from '../assets/clinaddis.png';
import clinaddis1 from '../assets/clinaddis1.png';
import clinaddis2 from '../assets/clinaddis2.png';
import clinaddis3 from '../assets/clinaddis3.png';

// Import your partner logo images
import mohLogo from '../assets/MOHlogo.png';
import kikundi from '../assets/kikundi.png';
import chakaCoffeeLogo from '../assets/chakalogo.png';
import SIT from '../assets/SITlogo.png';

const Home = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Add your image URLs here
  const heroImages = [clinaddis, clinaddis1, clinaddis2, clinaddis3];
  
  
  
  // Partner data with logo images and custom sizes
  const partners = [
    { logo: mohLogo, size: 'large' },
    { logo: kikundi, size: 'xxlarge' },
    { logo: chakaCoffeeLogo, size: 'mediumLarge' },
    { logo: SIT, size: 'mediumLarge' }
  ];
  
  // Featured Programs Data with MOH Logo as JSX
  const featuredPrograms = [
    { 
      title: 'School Program', 
      desc: 'Educational workshops and activities for young learners', 
      icon: '🏫'
    },
    { 
      title: 'Art & Medicine', 
      desc: 'Creative expression in healthcare', 
      icon: '🎨' 
    },
    { 
      title: 'MoH Monthly Series', 
      desc: 'Collaborative discussions with Ministry of Health', 
      icon: <img src={mohLogo} alt="MOH Logo" className="w-20 h-20 sm:w-22 sm:h-22 md:w-22 md:h-22 object-contain" />
    }
  ];
  
  // Auto-advance the carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000); // Change image every 4 seconds
    
    return () => clearInterval(interval);
  }, [heroImages.length]);

  return (
    <div className="home">
      {/* Hero Section with Auto-Changing Background Images */}
      <section className="hero relative overflow-hidden text-center text-white py-12 md:py-16 lg:py-20">
        {/* Auto-Changing Background Images with Gradient Overlay */}
        <div className="absolute inset-0 z-[-1]">
          {heroImages.map((image, index) => (
            <div 
              key={index}
              className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out ${
                index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
              style={{
                backgroundImage: `url(${image})`,
              }}
            />
          ))}
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary opacity-70" />
        </div>
        
        <div className="container relative z-10 px-4 sm:px-6">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3 md:mb-4">
            A Hub For Health, Ideas and Impact
          </h1>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-6 md:mb-8 max-w-2xl mx-auto">
            Bridging medicine, innovation, and youth creativity to transform healthcare
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link 
              to="/programs" 
              className="btn btn-primary font-bold px-4 py-2 sm:px-6 sm:py-3 md:px-8 md:py-3 text-sm sm:text-base"
            >
              Explore Programs
            </Link>
            <Link 
              to="/join" 
              className="btn border-2 border-white text-white font-bold px-4 py-2 sm:px-6 sm:py-3 md:px-8 md:py-3 hover:bg-white hover:text-primary transition-colors text-sm sm:text-base"
            >
              Join the Platform
            </Link>
          </div>
          
          {/* Image Indicators (Dots) */}
          <div className="flex justify-center gap-2 sm:gap-3 mt-8 md:mt-12">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full border-2 border-white transition-all ${
                  index === currentImageIndex ? 'bg-white' : 'bg-transparent'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Featured Programs */}
      <section className="section py-8 md:py-12 lg:py-16">
        <div className="container px-4 sm:px-6">
          <h2 className="text-center text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-primary mb-6 md:mb-8 lg:mb-12">
            Featured Programs
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {featuredPrograms.map((program, index) => (
              <div 
                key={index} 
                className="bg-white p-4 sm:p-6 md:p-8 rounded-lg shadow-sm sm:shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="text-3xl sm:text-4xl md:text-5xl mb-4 sm:mb-6 flex justify-center items-center">
                  {program.icon}
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-semibold mb-2 sm:mb-3">
                  {program.title}
                </h3>
                <p className="text-xs sm:text-sm md:text-base text-gray-600">
                  {program.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="section section-alt py-8 md:py-12 lg:py-16">
        <div className="container px-4 sm:px-6">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary mb-3 md:mb-4">
              Partners
            </h2>
            <p className="lead text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Collaborating with leading organizations for greater impact
            </p>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 md:gap-8 lg:gap-12 my-6 md:my-8">
            {partners.map((partner, index) => {
              // Responsive size mapping for logos
              const sizeClasses = {
                small: 'w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24',
                medium: 'w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32',
                mediumLarge: 'w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-36 lg:h-36',
                large: 'w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-40 lg:h-40',
                xlarge: 'w-32 h-32 sm:w-36 sm:h-36 md:w-40 md:h-40 lg:w-44 lg:h-44',
                xxlarge: 'w-36 h-36 sm:w-40 sm:h-40 md:w-44 md:h-44 lg:w-48 lg:h-48'
              };
              
              const textSizeClasses = {
                small: 'text-xs sm:text-sm',
                medium: 'text-sm sm:text-base',
                mediumLarge: 'text-sm sm:text-base md:text-lg font-medium',
                large: 'text-base sm:text-lg font-medium md:font-semibold',
                xlarge: 'text-lg sm:text-xl font-semibold',
                xxlarge: 'text-lg sm:text-xl md:text-2xl font-bold'
              };

              const maxWidthClasses = {
                small: 'max-w-[120px] sm:max-w-[140px] md:max-w-[150px]',
                medium: 'max-w-[140px] sm:max-w-[160px] md:max-w-[180px]',
                mediumLarge: 'max-w-[150px] sm:max-w-[170px] md:max-w-[190px]',
                large: 'max-w-[160px] sm:max-w-[180px] md:max-w-[200px]',
                xlarge: 'max-w-[170px] sm:max-w-[190px] md:max-w-[220px]',
                xxlarge: 'max-w-[180px] sm:max-w-[200px] md:max-w-[240px]'
              };
              
              return (
                <div 
                  key={index} 
                  className={`flex flex-col items-center p-3 sm:p-4 md:p-6 rounded-lg bg-white/10 hover:shadow-md sm:hover:shadow-lg hover:-translate-y-1 sm:hover:-translate-y-2 transition-all duration-300 ${
                    maxWidthClasses[partner.size] || maxWidthClasses.medium
                  }`}
                >
                  <div className={`${sizeClasses[partner.size] || sizeClasses.medium} flex items-center justify-center mb-3 sm:mb-4 md:mb-6 p-1 sm:p-2`}>
                    <img 
                      src={partner.logo} 
                      alt={`Partner logo ${index + 1}`}
                      className="max-w-full max-h-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                    />
                  </div>
                  <h5 className={`${textSizeClasses[partner.size] || textSizeClasses.medium} text-center text-dark`}>
                    {partner.name}
                  </h5>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;